import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChatHeader } from '../components/ChatHeader';
import { ChatHistory } from '../components/ChatHistory';
import { ChatInput } from '../components/ChatInput';
import { NavigationDrawer } from '../components/NavigationDrawer';
import { useChat } from '../hooks/useChat';
import { useOTPAuth } from '../hooks/useOTPAuth';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { useBalance } from '../hooks/useBalance';

export const ChatPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { balance, isLowBalance, isZeroBalance, refreshBalance } = useBalance();
  const { messages, isLoading, error, sendMessage, currentSessionId, startNewSession, loadSession } = useChat({
    onMessageReceived: () => {
      // Refresh balance after each response from Ella
      refreshBalance();
    },
  });
  const { isAuthenticated, isAnonymous, user, logout, createAnonymousSession, reloadUser } = useOTPAuth();

  // Monitor session timeout and redirect to login after 8 hours of inactivity
  useSessionTimeout();

  // Mobile drawer state
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  // Store session refresh function
  const [refreshSessionsFn, setRefreshSessionsFn] = useState<(() => Promise<void>) | null>(null);

  // Track drawer pinned state to adjust layout (desktop only)
  const [isDrawerPinned, setIsDrawerPinned] = useState<boolean>(() => {
    const saved = localStorage.getItem('navDrawerPinned');
    return saved === 'true';
  });

  // Track if we're on mobile
  const [isMobile, setIsMobile] = useState(false);

  // Track email request message
  const [emailRequestMessage, setEmailRequestMessage] = useState<string | undefined>();

  // Rotating welcome messages
  const welcomeMessages = [
    "Turn your hospitality insights into compelling deliverables. Pair your expertise with comprehensive data to create presentation-ready reports sent straight to your inbox.",
    "Leverage your industry knowledge with instant market intelligence. Transform your expertise into polished, client-ready reports via email.",
    "Your knowledge, amplified by AI. Ask questions leveraging your industry experience and receive expert-level reports with your insights formatted for clients.",
    "Apply your HTL expertise to proprietary market data. Generate professional reports that reflect your analysis and insights—emailed and ready to share.",
    "Combine your HTL expertise with AI-powered data analysis. Ask questions and receive professionally formatted reports that showcase your insights—delivered to your inbox.",
    "Your experience meets powerful analytics. Ask HTL questions drawing on your expertise and receive detailed professional reports delivered by email."
  ];

  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [prevMessageIndex, setPrevMessageIndex] = useState(welcomeMessages.length - 1);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleEmailRequest = (message: string) => {
    setEmailRequestMessage(message);
    // Clear after a brief moment to allow the effect to trigger
    setTimeout(() => setEmailRequestMessage(undefined), 100);
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Listen for storage changes to keep in sync (desktop only)
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('navDrawerPinned');
      setIsDrawerPinned(saved === 'true');
    };

    window.addEventListener('storage', handleStorageChange);

    // Also check periodically in case localStorage was updated in same tab
    const interval = setInterval(handleStorageChange, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Rotate welcome messages with cross-fade transition
  useEffect(() => {
    // Only rotate if there are no messages (new chat state)
    if (messages.length > 0) return;

    const rotationInterval = setInterval(() => {
      // Start the transition (fade out current, fade in next)
      setIsTransitioning(true);

      // Update to next message
      setPrevMessageIndex(currentMessageIndex);
      setCurrentMessageIndex((prev) => (prev + 1) % welcomeMessages.length);

      // Reset transition state after the fade completes
      setTimeout(() => {
        setIsTransitioning(false);
      }, 1000); // Match the transition duration
    }, 14000); // Show each message for 14 seconds (enough time to read)

    return () => clearInterval(rotationInterval);
  }, [messages.length, welcomeMessages.length, currentMessageIndex]);

  // Handle loading session from navigation state (e.g., from manage-plan page)
  useEffect(() => {
    const state = location.state as { loadSessionId?: string } | null;
    if (state?.loadSessionId) {
      loadSession(state.loadSessionId);
      // Clear the state to prevent reloading on refresh
      navigate('/', { replace: true, state: {} });
    }
  }, [location.state]);

  // Calculate left margin based on drawer state and authentication (desktop only)
  const showDrawer = isAuthenticated && !isAnonymous;
  const leftMargin = !isMobile && showDrawer ? (isDrawerPinned ? 'md:ml-64' : 'md:ml-16') : '';

  const handleNewChat = async () => {
    await startNewSession();
  };

  const handleLoadSession = async (sessionId: string) => {
    await loadSession(sessionId);
  };

  const handleSendMessage = async (message: string) => {
    // Track if this is the first message in the session
    const isFirstMessage = messages.length === 0;

    // Send the message
    await sendMessage(message);

    // If it was the first message, refresh sessions after a short delay
    // to allow the backend to create the session info
    if (isFirstMessage && refreshSessionsFn) {
      setTimeout(() => {
        refreshSessionsFn();
      }, 500);
    }
  };

  const handleLogout = async () => {
    await logout();
    // Create a new anonymous session after logout so user can continue chatting
    await createAnonymousSession();
    // Start a new chat session
    await startNewSession();
  };

  const handleRefreshSessions = useCallback((fn: () => Promise<void>) => {
    setRefreshSessionsFn(() => fn);
  }, []);

  return (
    <>
      <NavigationDrawer
        isAuthenticated={isAuthenticated}
        isAnonymous={isAnonymous}
        isMobileOpen={isMobileDrawerOpen}
        onMobileClose={() => setIsMobileDrawerOpen(false)}
        user={user}
        onNewChat={handleNewChat}
        onLoadSession={handleLoadSession}
        currentSessionId={currentSessionId}
        onLogout={handleLogout}
        balance={balance}
        isLowBalance={isLowBalance}
        isZeroBalance={isZeroBalance}
        onRefreshSessions={handleRefreshSessions}
        onUserUpdate={reloadUser}
      />

      <div
        className={`flex flex-col bg-neutral-white dark:bg-neutral-black transition-all duration-300 ${leftMargin}`}
        style={{ height: 'var(--viewport-height)' }}
      >
        <ChatHeader
          onMenuClick={() => setIsMobileDrawerOpen(true)}
          showMenuButton={showDrawer && isMobile}
        />

        {messages.length === 0 && !isMobile ? (
          /* Empty state on desktop - welcome message at top with centered input */
          <div className="flex-1 flex flex-col px-4 pt-16">
            <div className="w-full max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-h1 font-semibold text-neutral-black dark:text-neutral-white mb-6 tracking-tight">
                  Welcome to ChatHTL
                </h2>
                <div className="relative max-w-2xl mx-auto" style={{ minHeight: '120px' }}>
                  {/* Previous message fading out */}
                  <p
                    className="absolute inset-0 text-lg text-neutral-medium-gray dark:text-neutral-medium-gray leading-relaxed transition-opacity duration-1000"
                    style={{ opacity: isTransitioning ? 0 : 1 }}
                  >
                    {welcomeMessages[prevMessageIndex]}
                  </p>
                  {/* Current message fading in */}
                  <p
                    className="absolute inset-0 text-lg text-neutral-medium-gray dark:text-neutral-medium-gray leading-relaxed transition-opacity duration-1000"
                    style={{ opacity: isTransitioning ? 1 : 0 }}
                  >
                    {welcomeMessages[currentMessageIndex]}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="w-full max-w-3xl">
                <ChatInput onSendMessage={handleSendMessage} disabled={isLoading} initialMessage={emailRequestMessage} isAnonymous={isAnonymous} showDisclaimer={false} />
              </div>
            </div>
            <div className="pb-6" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
              <p className="text-caption text-neutral-medium-gray dark:text-neutral-medium-gray text-center">
                ChatHTL may produce inaccurate information. Verify important details.
              </p>
            </div>
          </div>
        ) : (
          /* Has messages or mobile view - normal layout */
          <>
            <ChatHistory
              messages={messages}
              isLoading={isLoading}
              onEmailRequest={handleEmailRequest}
              currentWelcomeMessage={welcomeMessages[currentMessageIndex]}
              prevWelcomeMessage={welcomeMessages[prevMessageIndex]}
              isWelcomeTransitioning={isTransitioning}
            />

            {error && (
              <div className="px-6 py-3 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
                <div className="max-w-4xl mx-auto">
                  <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                    Error: {error}
                  </p>
                </div>
              </div>
            )}

            <ChatInput onSendMessage={handleSendMessage} disabled={isLoading} initialMessage={emailRequestMessage} isAnonymous={isAnonymous} />
          </>
        )}
      </div>
    </>
  );
};
